import type { Type } from '@angular/core';
import type { FormlyFieldConfig } from '@ngx-formly/core';

import { AddressDisplayComponent } from './displays/address-display.component';
import { ColorDisplayComponent } from './displays/color-display.component';
import { CompositeDisplayComponent } from './displays/composite-display.component';
import { EmailTagsDisplayComponent } from './displays/email-tags-display.component';
import { GeolocationDisplayComponent } from './displays/geolocation-display.component';
import { HtmlDisplayComponent } from './displays/html-display.component';
import { IconDisplayComponent } from './displays/icon-display.component';
import { IdentityDocumentDisplayComponent } from './displays/identity-document-display.component';
import { KvkDisplayComponent } from './displays/kvk-display.component';
import { LicensePlateDisplayComponent } from './displays/license-plate-display.component';
import { LinkDisplayComponent } from './displays/link-display.component';
import { LinkedEntryDisplayComponent } from './displays/linked-entry-display.component';
import { ModuleSelectDisplayComponent } from './displays/module-select-display.component';
import { RatingDisplayComponent } from './displays/rating-display.component';
import { SecureInputDisplayComponent } from './displays/secure-input-display.component';
import { StatusDisplayComponent } from './displays/status-display.component';
import { UploadFileDisplayComponent } from './displays/upload-file-display.component';
import { UserRolesDisplayComponent } from './displays/user-roles-display.component';
import { ValueDisplayComponent } from './displays/value-display.component';

/**
 * Read-mode display components by name (#21239).
 *
 * The extension point for showing an answer as something other than text: a link for an
 * address you can write to, a badge for a status, a licence plate for a vehicle, rendered
 * markup for rich text. Adding one is a single entry here plus the component — the row, the
 * wrapper and the extension stay untouched.
 *
 * Keyed by NAME rather than by Formly type so a module admin can override the display per field
 * (`props.readonlyDisplay`) without a code change, and so two field types can share one display
 * (`custom-select` and `select` do). Every key here doubles as such an override name.
 */
export const READONLY_DISPLAYS: Readonly<Record<string, Type<unknown>>> = {
  value: ValueDisplayComponent,
  // Both link families: `email` is its own type, `url`/`tel` are an `input` with a props.type —
  // the display decides which of the three (if any) applies, everything else stays text.
  email: LinkDisplayComponent,
  input: LinkDisplayComponent,
  // Multiple addresses (#21239, task 6): one mailto: link per stored address, same idea as
  // `email` above but per item — see `EmailTagsDisplayComponent` for why it is not a third
  // variant of `LinkDisplayComponent`.
  'email-tags': EmailTagsDisplayComponent,
  'custom-select': StatusDisplayComponent,
  select: StatusDisplayComponent,
  rdw: LicensePlateDisplayComponent,
  geolocation: GeolocationDisplayComponent,
  'module-select': ModuleSelectDisplayComponent,
  editor: HtmlDisplayComponent,
  // Stored tokens ('heroicons_outline:truck', '#ff0000') that mean nothing printed as text.
  iconpicker: IconDisplayComponent,
  colorpicker: ColorDisplayComponent,
  // A star count on its own does not say out of how many; the display appends the same scale
  // `rating.component.ts` uses to build its star row (task 7, #21239).
  rating: RatingDisplayComponent,
  // The masked reminder ('••••2735') with the answer of the configured prefix field in front of
  // it; the secret itself never reaches the read view (secure-input-mask.util.ts).
  'secure-input': SecureInputDisplayComponent,
  // Same composite-answer construction as secure-input (the masked reminder built by
  // secureDisplayText), but its own display: the type label lives INSIDE this field's own value
  // (`{ type, number }`), not on a sibling field the display would have to look up.
  'identity-document': IdentityDocumentDisplayComponent,
  // Both upload fields read as the Figma "Upload card" (task 21243); the display resolves where
  // each type keeps its files.
  upload: UploadFileDisplayComponent,
  'upload-document-entry': UploadFileDisplayComponent,
  // Stores role ids; the display maps them to names from formState.userRoles.
  'custom-userroles-multicheckbox': UserRolesDisplayComponent,
  // The seven date/hours COMPOSITES (task 5). Their answer is an object whose meaning lives in
  // the sub-field LABELS (`fieldGroup.config.ts`), which the generic value-to-text join cannot
  // reach — it drops every nested object and joins what is left unlabelled ('40 8.5' for a whole
  // availability schedule). One display for all seven, per the user decision of 2026-09-21.
  // `composite` doubles as the override name for a field that stores the same kind of answer.
  availability: CompositeDisplayComponent,
  cron: CompositeDisplayComponent,
  week: CompositeDisplayComponent,
  week_time: CompositeDisplayComponent,
  date_hours_calculator: CompositeDisplayComponent,
  'week-day-hours-number': CompositeDisplayComponent,
  'week-day-hours-time': CompositeDisplayComponent,
  composite: CompositeDisplayComponent,
  // The two types whose stored answer really is a linked-entry envelope; the display itself
  // documents the per-type evidence, including why the other "Gelinkde velden" types are absent.
  // `linked-entry` doubles as the override name for a field that stores an envelope under a type
  // whose slot is taken (a module-backed `select`/`custom-select`, which the status display owns).
  'select-linked-entry': LinkedEntryDisplayComponent,
  autocomplete: LinkedEntryDisplayComponent,
  'linked-entry': LinkedEntryDisplayComponent,
  // The BAG/KvK check the edit components show once an answer was verified — moved OUT of the
  // text-fallback bucket in readonly.types.ts; see verification.util.ts for the mirrored
  // conditions.
  address: AddressDisplayComponent,
  kvk: KvkDisplayComponent,
};

/**
 * Which display renders this field: `props.readonlyDisplay` (per-field override) → the display
 * registered for the field's type → the text display.
 *
 * Never throws. Both lookups are CONFIG — a module document names them — so a typo or a display
 * removed from the registry falls through to the next step instead of taking the read view down
 * with it.
 */
export function resolveReadonlyDisplay(field: FormlyFieldConfig): Type<unknown> {
  const configured = field.props?.['readonlyDisplay'];
  const byName = typeof configured === 'string' ? registered(configured) : undefined;
  const byType = typeof field.type === 'string' ? registered(field.type) : undefined;
  return byName ?? byType ?? ValueDisplayComponent;
}

/**
 * `Object.hasOwn`, not a bare index read: the key is config, so it can be any string a module
 * document carries. `READONLY_DISPLAYS['constructor']` resolves to a Function through the
 * prototype chain, and handing that to `NgComponentOutlet` renders nothing at best.
 */
function registered(name: string): Type<unknown> | undefined {
  return Object.hasOwn(READONLY_DISPLAYS, name) ? READONLY_DISPLAYS[name] : undefined;
}
